import { Tooltip } from 'antd';
import clsx from 'clsx';
import { Monitor, Moon, Sun, type LucideIcon } from 'lucide-react';
import type { ColorModePreference } from './color-mode';
import { useColorMode } from './ColorModeProvider';

const OPTIONS: { value: ColorModePreference; label: string; icon: LucideIcon }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'Match system', icon: Monitor },
];

interface ColorModeToggleProps {
  /** `compact` shows one button that cycles light → dark → system, for the collapsed rail. */
  compact?: boolean;
  /** Larger targets for the one-handed yard surface. */
  size?: 'small' | 'large';
}

export function ColorModeToggle({ compact = false, size = 'small' }: ColorModeToggleProps) {
  const { preference, setPreference } = useColorMode();
  const button = size === 'large' ? 'size-9' : 'size-7';

  if (compact) {
    const index = OPTIONS.findIndex((o) => o.value === preference);
    const { icon: Icon, label } = OPTIONS[index];
    const next = OPTIONS[(index + 1) % OPTIONS.length];
    return (
      <Tooltip title={`Theme: ${label}`} placement="right">
        <button
          type="button"
          aria-label={`Colour theme: ${label}. Switch to ${next.label}`}
          onClick={() => setPreference(next.value)}
          className={clsx(button, 'grid place-items-center rounded-md text-muted transition-colors hover:bg-sider-selected hover:text-fg')}
        >
          <Icon className="size-4" />
        </button>
      </Tooltip>
    );
  }

  return (
    <div role="radiogroup" aria-label="Colour theme" className="inline-flex rounded-lg bg-page p-0.5 ring-1 ring-line">
      {OPTIONS.map(({ value, label, icon: Icon }) => (
        <Tooltip key={value} title={label}>
          <button
            type="button"
            role="radio"
            aria-checked={preference === value}
            aria-label={label}
            onClick={() => setPreference(value)}
            className={clsx(
              button,
              'grid place-items-center rounded-md transition-colors',
              preference === value ? 'bg-surface text-fg shadow-sm ring-1 ring-line' : 'text-muted hover:text-fg',
            )}
          >
            <Icon className="size-3.5" />
          </button>
        </Tooltip>
      ))}
    </div>
  );
}
